import { api } from "../lib";
import type { FragmentCountQualifier } from "../utils/fragmentCount";

export type InternalGrossCodeStatus =
  | "pending_review"
  | "approved"
  | "rejected";

export interface InternalGrossCode {
  id: string;
  code: string;
  description: string;
  status: InternalGrossCodeStatus;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  reviewed_by: string | null;
  reviewed_at: string | null;
  rejection_reason: string | null;
  scenario_description: string | null;
  is_active: boolean;
  fragment_count?: number | null;
  fragmentCount?: number | null;
  fragment_count_qualifier?: FragmentCountQualifier | null;
  fragmentCountQualifier?: FragmentCountQualifier | null;
}

export interface InternalGrossCodeWithUser extends InternalGrossCode {
  created_by_user?: { full_name: string };
  reviewed_by_user?: { full_name: string };
}

export interface CreateGrossCodeParams {
  code: string;
  description: string;
  scenario_description: string;
  created_by: string;
  fragment_count?: number | null;
  fragment_count_qualifier?: FragmentCountQualifier | null;
}

export interface UpdateGrossCodeParams {
  code?: string;
  description?: string;
  scenario_description?: string;
  is_active?: boolean;
  fragment_count?: number | null;
  fragment_count_qualifier?: FragmentCountQualifier | null;
}

const basePath = "/admin/internal-gross-codes";

export async function getInternalGrossCodes(filters?: {
  status?: string;
  search?: string;
  includeInactive?: boolean;
}): Promise<InternalGrossCodeWithUser[]> {
  return api.get<InternalGrossCodeWithUser[]>(basePath, {
    params: filters,
  });
}

export async function getApprovedGrossCodes(): Promise<InternalGrossCode[]> {
  return api.get<InternalGrossCode[]>(`${basePath}/approved`);
}

export async function getInternalGrossCodeById(
  id: string
): Promise<InternalGrossCodeWithUser | null> {
  return api.get<InternalGrossCodeWithUser>(`${basePath}/${id}`);
}

export async function createInternalGrossCode(
  params: CreateGrossCodeParams
): Promise<InternalGrossCode> {
  return api.post<InternalGrossCode>(basePath, params);
}

export async function updateInternalGrossCode(
  id: string,
  params: UpdateGrossCodeParams
): Promise<InternalGrossCode> {
  return api.put<InternalGrossCode>(`${basePath}/${id}`, params);
}

export async function approveInternalGrossCode(
  id: string,
  userId: string
): Promise<InternalGrossCode> {
  return api.post<InternalGrossCode>(`${basePath}/${id}/approve`, { userId });
}

export async function rejectInternalGrossCode(
  id: string,
  userId: string,
  reason: string
): Promise<InternalGrossCode> {
  return api.post<InternalGrossCode>(`${basePath}/${id}/reject`, {
    userId,
    reason,
  });
}

export async function deleteInternalGrossCode(id: string): Promise<void> {
  await api.delete<void>(`${basePath}/${id}`);
}

export function parseVariables(description: string): string[] {
  const variableRegex = /\{\{([^}]+)\}\}/g;
  const variables: string[] = [];
  let match;

  // eslint-disable-next-line no-cond-assign
  while ((match = variableRegex.exec(description)) !== null) {
    const variableName = match[1].trim();
    if (!variables.includes(variableName)) {
      variables.push(variableName);
    }
  }

  return variables;
}

export function replaceVariables(
  description: string,
  variables: Record<string, string>
): string {
  let result = description;

  Object.entries(variables).forEach(([key, value]) => {
    const escapedKey = key.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const regex = new RegExp(`\\{\\{\\s*${escapedKey}\\s*\\}\\}`, "g");
    result = result.replace(regex, value);
  });

  return result;
}
